import React, { useState, useMemo, useCallback } from "react";
import { Box, IconButton, Typography, Button, Tooltip } from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import { groupBy, uniq } from "lodash";
import { useNavigate } from "react-router-dom";
import { helpers, format } from "../../common/lib";
import { CsvError } from "../components";
import { strings } from "../../common/i18n";
import actions from "../../Store/actions";
import EditIcon from "@mui/icons-material/Edit";
import { logToSentry } from "../../config/Sentry";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddIcon from "@mui/icons-material/Add";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import styles from "../styles";
import { dateRangeFilterFn } from "../../common/components/MaterialReactTable/filterFns";

/**
 * Special filter for last login that handles auditor logic
 */
const lastLoginFilterFn = (row, columnId, filterValue) => {
    const lastLogin = row.getValue(columnId);
    const groups = row.original.groups;
    const isAuditor = groups && groups.includes("Auditor");

    // If no date filter is applied, show all rows
    if (!filterValue) return true;

    // If user is not an auditor and has no last login date
    if (!isAuditor || !lastLogin) {
        // Only show non-auditors when no date range is specified
        // or when specifically looking for empty dates
        return false; // Hide non-auditors when date filter is active
    }

    // For auditors with last login dates, apply normal date range filter
    return dateRangeFilterFn(row, columnId, filterValue);
};

/**
 *  Hook for user table data and operations
 */
export default () => {
    const {
        accounts,
        auth,
        locations,
        typesList,
        userLocations,
        userOrganisations
    } = useSelector((state) => state);
    const dispatch = useDispatch();

    const [currentSelectedRow, setCurrentSelectedRow] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [error, setError] = useState(false);

    const [optionOpen, setOptionsOpen] = useState(null);
    /** On menu open close */
    const onMenuClose = () => setOptionsOpen(null);
    const onMenuOpen = (event) => setOptionsOpen(event.currentTarget);

    let navigate = useNavigate();

    const handleDeleteDialogOpen = (row) => {
        setCurrentSelectedRow(row);
        setDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialog(false);
    };

    /** Editing row */
    const onEdit = (row: any) => {
        try {
            if (row.id) {
                navigate(`${row.id}`);
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On edit user:", { error });
            dispatch(actions.toast.create.error(strings("errors.onEditUser")));
        }
    };

    const handleRemoveUser = async () => {
        try {
            if (currentSelectedRow.id) {
                await dispatch(
                    actions.accounts.create.deleteRequest({
                        userId: currentSelectedRow.id,
                        organisationId: userOrganisations.item.id
                    })
                );
                await dispatch(actions.accounts.create.getRequest());
                dispatch(
                    actions.toast.create.success(
                        strings("messages.removeUserSuccess")
                    )
                );
                handleDeleteDialogClose();
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On deleting user:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onDeleteUser"))
            );
        }
    };

    /** Get unique groups for filter options */
    const uniqueGroups = useMemo(() => {
        const allGroups = accounts.data.flatMap((item) =>
            item.groups ? helpers.nameSplit(helpers.nameJoin(item.groups)) : []
        );
        return uniq(allGroups.map((group) => group.name)).filter(Boolean);
    }, [accounts.data]);

    /** Columns for the data */
    const columns = useMemo(
        () => [
            {
                id: "email",
                header: strings("email"),
                accessorKey: "email",
                hideable: false,
                filterVariant: "autocomplete",
                size: 150
            },
            {
                id: "displayName",
                header: strings("displayName"),
                accessorKey: "displayName",
                filterVariant: "autocomplete",
                size: 150
            },
            {
                id: "firstName",
                header: strings("firstName"),
                accessorKey: "firstName",
                filterVariant: "autocomplete",
                size: 150
            },
            {
                id: "surname",
                header: strings("surname"),
                accessorKey: "surname",
                filterVariant: "autocomplete",
                size: 150
            },
            {
                id: "groups",
                header: strings("groups"),
                accessorKey: "groups",
                filterVariant: "multi-select",
                filterSelectOptions: uniqueGroups,
                filterFn: (row, columnId, filterValue) => {
                    if (!filterValue || filterValue.length === 0) return true;

                    const groupsString = row.getValue(columnId);
                    if (!groupsString) return false;

                    return filterValue.some((filterGroup) =>
                        groupsString.includes(filterGroup)
                    );
                },
                Cell: ({ cell }) => {
                    const groups = cell.getValue();
                    return groups || "-";
                },
                size: 200
            },
            {
                id: "locationRef",
                header: strings("locationRef"),
                accessorKey: "locationRef",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            },
            {
                id: "lastLogin",
                header: strings("lastLogin"),
                accessorKey: "lastLogin",
                filterVariant: "date-range",
                // Use the original value (Date object) for filtering
                accessorFn: (originalRow) => {
                    // Return the Date object for filtering, null for display logic
                    return originalRow.lastLogin;
                },
                Cell: ({ cell, row }) => {
                    const lastLogin = cell.getValue();
                    const groups = row.original.groups;
                    const isAuditor = groups && groups.includes("Auditor");

                    // Only show last login for auditors who have a last login date
                    if (!isAuditor || !lastLogin) return "-";

                    return format.dateTimeNew(lastLogin);
                },
                sortingFn: "datetime",
                filterFn: lastLoginFilterFn, // Use the specialized filter
                size: 150
            }
        ],
        [uniqueGroups]
    );

    /** Table data - Fixed to ensure proper Date objects */
    const data = useMemo(() => {
        const byUser = groupBy(userLocations.data, "user");
        return accounts.data.map((item) => {
            const next = {
                ...item,
                firstName: item.firstName,
                groups: helpers.nameJoin(item.groups),
                // Ensure lastLogin is a proper Date object or null
                lastLogin: item.lastLogin ? new Date(item.lastLogin) : null
            };

            const locationRefs = {};
            byUser[next.id] &&
                byUser[next.id].forEach(({ location }) => {
                    const index = locations.byId[location];
                    if (index == null) {
                        console.error(
                            `user[${next.id}].location[${location}] not found`
                        );
                    } else {
                        if (
                            locations.data[index] &&
                            locations.data[index].locationRef
                        )
                            locations.data[index].locationRef.forEach(
                                (item) => {
                                    if (item.name !== "")
                                        locationRefs[item.name] = item.name;
                                }
                            );
                    }
                });

            const refs = Object.keys(locationRefs);
            next.locationRef =
                refs.length > 4
                    ? `;${refs.join(";")}...`
                    : `;${refs.join(";")}`;

            return next;
        });
    }, [accounts, auth, locations, userLocations]);

    // ... rest of your existing code remains the same
    /** On mapping the location id  */
    const locationIdRefMap = useMemo(
        () => helpers.idNameMap(locations.data, "locationRef"),
        [locations]
    );

    /** On csv validation at the time of upload  */
    const onCsvMap = useCallback(
        ({
            displayName,
            email,
            firstName,
            groups,
            locationRef,
            row,
            surname
        }) => {
            const next = {
                displayName,
                email,
                errors: [],
                firstName,
                groups,
                groupIds: [],
                locationRef: [],
                locations: [],
                surname
            };
            if (!displayName) {
                next.errors.push(
                    <CsvError
                        key={`${row}-displayName`}
                        error={`${strings("errors.fieldRequired")} [displayName]`}
                        row={row}
                    />
                );
                setError(true);
            }
            if (!email) {
                next.errors.push(
                    <CsvError
                        key={`${row}-email`}
                        error={`${strings("errors.fieldRequired")} [email]`}
                        row={row}
                    />
                );
                setError(true);
            }
            if (!firstName) {
                next.errors.push(
                    <CsvError
                        key={`${row}-firstName`}
                        error={`${strings("errors.fieldRequired")} [firstName]`}
                        row={row}
                    />
                );
                setError(true);
            }

            if (!groups) {
                next.errors.push(
                    <CsvError
                        key={`${row}-groups`}
                        error={`${strings("errors.fieldRequired")} [groups]`}
                        row={row}
                    />
                );
                setError(true);
            }

            if (!surname) {
                next.errors.push(
                    <CsvError
                        key={`${row}-surname`}
                        error={`${strings("errors.fieldRequired")} [surname]`}
                        row={row}
                    />
                );
                setError(true);
            }
            helpers.nameSplit(groups).forEach(({ name }) => {
                const group = typesList.groups.data.find(
                    (item) => item.name === name
                );
                if (!group) {
                    next.errors.push(
                        <CsvError
                            key={`${row}-group-${name}`}
                            error={`${strings("errors.groupInvalid")} [${name}]`}
                            row={row}
                        />
                    );
                    setError(true);
                } else {
                    next.groupIds.push(group.id);
                }
            });
            helpers.nameSplit(locationRef).forEach(({ name }) => {
                const locationId = locationIdRefMap[name];
                if (locationId)
                    next.locations = uniq([...next.locations, locationId]);
            });

            // assign locationRefs string
            next.locationRef = locationRef;

            // assignId of existing user
            const account = accounts.data.filter(
                (item) => item.email === email
            );
            if (account[0]) {
                next.id = account[0].id;
            }

            return next;
        },
        [locations, locationIdRefMap]
    );

    const onCsvUpload = () => setError(false);

    const onDataItemAdd = () => {
        dispatch(actions.accounts.create.itemAdd());
        navigate(`./create`);
    };

    const onDataItemClear = () => dispatch(actions.accounts.create.itemClear());

    const onDataItemSet = (item: any, index: number) => {
        const account = accounts.data[index];
        dispatch(actions.accounts.create.itemSet(account));
    };

    const onDataSave = async (next: Array<any>) => {
        try {
            const nextAccounts = next.map((item) => ({
                ...item,
                groups: item.groupIds
            }));

            const res = await dispatch(
                actions.accounts.create.postRequest(nextAccounts)
            );

            const nextUserLocations = res.accounts.reduce(
                (aggregate, { email, id }) => {
                    const account = next.find(
                        (item) =>
                            item.email === email &&
                            item.locations &&
                            item.locations.length != 0
                    );

                    if (!account || account.length < 1) return [...aggregate];

                    const locationsData = account.groupIds.map((group) => ({
                        group,
                        location: account.locations,
                        user: id
                    }));

                    return [...aggregate, ...locationsData];
                },
                []
            );

            await dispatch(
                actions.userLocations.create.postRequest(nextUserLocations)
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On user data save:", { error });
            dispatch(actions.toast.create.error(strings("errors.onUserSave")));
        }
    };

    /** Toolbar components for search  */
    const toolBarComponents = () => (
        <>
            <IconButton
                key="edit"
                title={strings("options")}
                onClick={onMenuOpen}
            >
                <MoreHorizIcon color="primary" />
            </IconButton>
            <Button
                sx={styles.primaryButton}
                variant="contained"
                size="small"
                color="primary"
                onClick={onDataItemAdd}
                endIcon={<AddIcon />}
            >
                {strings("addUser")}
            </Button>
        </>
    );

    /** Row Actions */
    const rowActions = (row) => (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}>
            <IconButton
                size="small"
                title={strings("edit")}
                onClick={() => onEdit(row.original)}
            >
                <Tooltip title={strings("edit")}>
                    <EditIcon fontSize="small" color="primary" />
                </Tooltip>
            </IconButton>
            <IconButton
                size="small"
                title={strings("delete")}
                onClick={() => handleDeleteDialogOpen(row.original)}
            >
                <Tooltip title={strings("delete")}>
                    <NotInterestedIcon fontSize="small" color="primary" />
                </Tooltip>
            </IconButton>
        </Box>
    );

    return {
        columns,
        data,
        deleteDialog,
        setDeleteDialog,
        handleDeleteDialogClose,
        handleDeleteDialogOpen,
        handleRemoveUser,
        currentSelectedRow,
        rowActions,
        error,
        onCsvMap,
        onCsvUpload,
        onDataItemAdd,
        onDataItemClear,
        onDataItemSet,
        onDataSave,
        optionOpen,
        onMenuOpen,
        onMenuClose,
        toolBarComponents
    };
};
